import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Images from '../../assests/images/Images';
import { INDUSTRIES_DIRECTORY } from './sectorDirectoryData';
import { navigateWithLoader } from '../../components/navigation/ScrollToTop';
import CtaButton from '../../components/common/CtaButton';
import './Industries.css';

/* ─── Scroll Reveal Hook (Same smooth viewport transition as Home & Services) ─── */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll(
      '.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt'
    );

    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    const checkScrollPosition = () => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= (window.innerHeight || document.documentElement.clientHeight) - 20) {
          el.classList.add('sipl-revealed');
        }
      });
    };

    checkScrollPosition();
    window.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
      observer.disconnect();
    };
  }, []);
};

/* ─── Data Arrays ─── */

const FAQS = [
  {
    q: 'Do you build solutions compliant with industry standards like HIPAA or GDPR?',
    a: 'Yes. We engineer applications with role-based access control (RBAC), end-to-end encryption, audit logs, and compliance standards tailored to healthcare, fintech, and enterprise requirements.',
  },
  {
    q: 'Can you integrate custom software with our existing legacy systems?',
    a: 'Yes. We build secure REST & GraphQL API bridges to connect new applications with your existing ERP (SAP, Oracle), EMR, POS, or legacy databases.',
  },
  {
    q: 'How long does a typical industry software project take?',
    a: 'Initial operational prototypes are usually delivered within 4 to 6 weeks, with full production releases rolling out in iterative 2-week sprints.',
  },
  {
    q: 'Do we own 100% of the custom software code?',
    a: 'Yes. Upon completion, 100% of the source code IP, documentation, and repository access are transferred to your organization.',
  },
];

/* ─── SECTION 1: HERO ─── */
const IndustriesHero = () => {
  const scrollToDirectory = () => {
    navigateWithLoader('industry-directory');
  };

  return (
    <section
      aria-labelledby="industries-hero-heading"
      className="py-4 border-bottom sipl-section-transparent sipl-hero"
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #ffffff 100%)',
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="container-fluid px-3 px-sm-4 px-lg-5 py-3" style={{ position: 'relative', zIndex: 1, maxWidth: '1680px' }}>
        <div className="row align-items-center g-4 g-xl-5">
          <div className="col-12 col-lg-5 sipl-reveal-ttb">
            <span
              className="text-uppercase fw-bold d-block mb-2"
              style={{ color: '#0284c7', fontSize: '0.8rem', letterSpacing: '0.12em' }}
            >
              INDUSTRIES WE BUILD FOR
            </span>

            <h1
              id="industries-hero-heading"
              className="mb-3"
              style={{
                fontSize: 'clamp(1.7rem, 2.75vw, 2.35rem)',
                letterSpacing: '-0.025em',
                lineHeight: 1.22,
                color: '#0f172a',
                fontWeight: 600,
              }}
            >
              <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Delivering Industry-Specific</span>
              <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Software That Drives</span>
              <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Real Results</span>
            </h1>

            <p className="mb-1" style={{ fontSize: '0.96rem', lineHeight: 1.68, maxWidth: 620, color: '#475569', margin: '0 0 1.5rem', textAlign: 'justify' }}>
              <span style={{ fontWeight: 700, color: '#1a2f6e' }}>At Seikural, </span>
              we build custom software tailored to each industry's unique challenges, workflows, and customer needs. From web and mobile apps to enterprise platforms, our solutions are designed to deliver measurable business outcomes.
            </p>

            <p className="mb-2" style={{ fontSize: '0.96rem', lineHeight: 1.68, maxWidth: 620, color: '#475569', margin: '0 0 1.5rem', textAlign: 'justify' }}>
              Whether it's healthcare, retail, logistics, or other industries, we combine domain expertise and proven technology to build solutions that make a real impact.
            </p>

            <div className="d-flex flex-wrap align-items-center gap-3">
              <CtaButton to="/contact" variant="primary">
                Discuss Your Sector
              </CtaButton>
              <CtaButton
                onClick={scrollToDirectory}
                variant="secondary"
                arrowDirection="down"
              >
                Explore Industry Index
              </CtaButton>
            </div>
          </div>

          <div className="col-12 col-lg-7 d-flex align-items-center justify-content-center sipl-reveal-ttb">
            <div
              className="sipl-hero-slider-wrapper w-100 position-relative"
              style={{
                height: '440px',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                boxShadow: '0 16px 36px -10px rgba(15, 23, 42, 0.16), 0 4px 12px rgba(15, 23, 42, 0.06)',
              }}
            >
              <img
                src={Images?.industryheader}
                alt="Industry-specific software solutions"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── SECTION 2: INDUSTRY DIRECTORY GRID ─── */
const ACCENT_COLORS = [
  { bar: 'linear-gradient(90deg,#1a2f6e,#0d9488)', icon: '#e8f4fd', iconColor: '#1a2f6e' },
  { bar: 'linear-gradient(90deg,#4caf50,#0d9488)', icon: '#e8f7ee', iconColor: '#2e7d32' },
  { bar: 'linear-gradient(90deg,#0d9488,#4caf50)', icon: '#e0f5f3', iconColor: '#0d9488' },
  { bar: 'linear-gradient(90deg,#f97316,#1a2f6e)', icon: '#fff3eb', iconColor: '#c2440e' },
];

const IndustryCard = ({ ind, index }) => {
  const accent = ACCENT_COLORS[index % ACCENT_COLORS.length];

  return (
    <div
      className="sipl-reveal-btt h-100"
      style={{ transitionDelay: `${index * 0.05}s` }}
    >
      <div
        className="sipl-industry-card-item"
        aria-label={`${ind.title} solutions`}
      >
        {/* Colored accent top bar */}
        <div className="sipl-industry-card-bar" style={{ background: accent.bar }} />

        <div className="sipl-industry-card-body">
          <div>
            {/* Icon */}
            <div
              className="sipl-industry-card-icon"
              style={{
                background: accent.icon,
                color: accent.iconColor,
              }}
            >
              {ind.icon}
            </div>

            {/* Title */}
            <h3 className="sipl-industry-card-title">
              {ind.title}
            </h3>

            {/* Description */}
            <p className="sipl-industry-card-desc">
              {ind.desc}
            </p>
          </div>

          {/* Tag badge */}
          <div className="sipl-industry-card-footer">
            <span
              className="sipl-industry-card-tag"
              style={{
                color: accent.iconColor,
                background: accent.icon,
              }}
            >
              {ind.tag}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const IndustryOverviewGrid = () => (
  <section
    id="industry-directory"
    className="py-5 sipl-section-tint-translucent sipl-industries-section-wrapper"
    aria-label="Industry Directory Grid"
  >
    <div className="container py-4">

      {/* Section heading */}
      <div className="text-center sipl-reveal-ttb mb-4 mb-md-5">
        <h2 className="fw-bold mb-3" style={{ fontSize: 'clamp(1.85rem, 3vw, 2.75rem)', lineHeight: 1.15, letterSpacing: '-0.03em', color: '#0f172a' }}>
          Industries We Specialize In
        </h2>
        <p className="text-muted mx-auto" style={{ lineHeight: '1.7', fontSize: '1rem', maxWidth: '680px' }}>
          From startups to large enterprises, we bring deep domain expertise and cutting-edge technology to every sector we serve.
        </p>
      </div>

      {/* Fully responsive CSS Grid */}
      <div className="sipl-industry-grid">
        {INDUSTRIES_DIRECTORY.map((ind, i) => (
          <IndustryCard key={ind.num} ind={ind} index={i} />
        ))}
      </div>

    </div>
  </section>
);

/* ─── SECTION 3: FREQUENTLY ASKED QUESTIONS ─── */
const IndustriesFAQ = () => (
  <section className="py-5 sipl-section-transparent border-top" aria-label="Frequently Asked Questions" style={{ position: 'relative', zIndex: 1 }}>
    <div className="container py-4">

      <div className="row justify-content-center mb-5">
        <div className="col-12 col-lg-8 text-center sipl-reveal-ttb">
          <span className="text-uppercase fw-bold sipl-text-teal small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
            GOT QUESTIONS?
          </span>
          <h2 className="display-6 fw-bold text-dark mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-muted" style={{ lineHeight: '1.75', fontSize: '0.98rem' }}>
            Common questions about engaging Seikural Innovation for industry software projects.
          </p>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-lg-9 sipl-reveal-btt">
          <div className="accordion" id="industriesFaqAccordion">
            {FAQS.map((faq, idx) => {
              const collapseId = `collapseIndFaq${idx}`;
              const headingId = `headingIndFaq${idx}`;
              return (
                <div key={faq.q} className="accordion-item border-0 mb-3 rounded-4 shadow-sm overflow-hidden">
                  <h3 className="accordion-header" id={headingId}>
                    <button
                      className={`accordion-button fw-bold text-dark p-4 ${idx !== 0 ? 'collapsed' : ''}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#${collapseId}`}
                      aria-expanded={idx === 0 ? 'true' : 'false'}
                      aria-controls={collapseId}
                      style={{ fontSize: '0.98rem', background: '#ffffff', boxShadow: 'none' }}
                    >
                      {faq.q}
                    </button>
                  </h3>
                  <div
                    id={collapseId}
                    className={`accordion-collapse collapse ${idx === 0 ? 'show' : ''}`}
                    aria-labelledby={headingId}
                    data-bs-parent="#industriesFaqAccordion"
                  >
                    <div className="accordion-body p-4 pt-0 text-muted" style={{ lineHeight: '1.75', fontSize: '0.95rem' }}>
                      {faq.a}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  </section>
);

/* ─── SECTION 6: CLOSING CTA ─── */
const IndustriesCTA = () => (
  <section
    className="py-5 sipl-section-transparent text-center position-relative border-top"
    aria-label="Start a Conversation"
    style={{ position: 'relative', zIndex: 1 }}
  >
    <div className="sipl-color-bar position-absolute top-0 start-0" />
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 sipl-reveal-ttb">

          <span
            className="text-uppercase fw-bold sipl-text-navy small d-block mb-3"
            style={{ letterSpacing: '0.12em', color: '#1a2f6e' }}
          >
            START A CONVERSATION
          </span>

          <h2 className="display-5 fw-bold text-dark mb-3">
            Ready to build software tailored to your industry?
          </h2>

          <p
            className="text-muted mb-4"
            style={{ lineHeight: '1.8', maxWidth: 640, margin: '0 auto 1.8rem auto', fontSize: '0.98rem' }}
          >
            Schedule an informal 15-minute intro call. We'll answer your technical questions, evaluate your sector requirements, and suggest a practical path forward.
          </p>

          <div className="d-flex justify-content-center">
            <CtaButton to="/contact" variant="primary">
              Talk to Our Technical Team
            </CtaButton>
          </div>

        </div>
      </div>
    </div>
  </section>
);

/* ─── MAIN EXPORT ─── */
const Industries = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Industries We Serve | Seikural Innovation Pvt. Ltd.';
    window.scrollTo(0, 0);
    return () => { document.title = prev; };
  }, []);

  useScrollReveal();

  return (
    <div className="sipl-home-page-wrapper">
      {/* Ambient background glow spots (Logo color palette) */}
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '1%', left: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(26,47,110,0.045) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '25%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(13,148,136,0.04) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '55%', left: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(76,175,80,0.035) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '80%', right: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(249,115,22,0.025) 0%, transparent 70%)' }} />

      {/* 1. Hero */}
      <IndustriesHero />

      {/* 2. Industry Directory Grid */}
      <IndustryOverviewGrid />

      {/* 3. Sector FAQs */}
      <IndustriesFAQ />

      {/* 4. Call to Action */}
      <IndustriesCTA />
    </div>
  );
};

export default Industries;
